/**
 * customObjects — the ONE place the portal knows what `/api/objects/` means.
 *
 * A custom object is a business noun an admin invents (Reservation, Patient,
 * Kitchen ticket, Consignment). The server owns the definition; three screens
 * render it (`pages/Objects/ObjectList|Form|Detail`) and the sidebar draws one
 * nav item per object. This module is what those four share, so the API shape
 * is read in one file rather than four.
 *
 * 🔴 THE FEATURE IS BEHIND A SERVER FLAG AND "OFF" IS A 404.
 * `object_views` answers `{"detail": "Not found."}` when `INIKRU_STUDIO` is
 * off, deliberately indistinguishable from a route that was never built — a
 * 403 would be a map of the unreleased product drawn for anyone who guesses a
 * URL. The client half of that contract is here: a failed fetch resolves to an
 * EMPTY LIST, never to an error banner, a console scream or a retry loop. A
 * portal with the flag off must look exactly like one that never heard of
 * custom objects.
 *
 * 🔴 THE TYPE LIST IS FETCHED ONCE PER PAGE LOAD, AT MODULE SCOPE.
 * All ~100 portal pages render their own `<Base>`, so `<Sidebar>` unmounts and
 * remounts on EVERY navigation (see the `savedNavScrollTop` note there). A
 * `useEffect` fetch in the sidebar would therefore fire on every route change.
 * The cache + subscriber pair below mirrors `utils/businessConfig`: state that
 * must outlive a remount lives in the module, and components subscribe.
 */
import React from 'react';
import axios from 'axios';
import {
  Box, Boxes, Package, PackageCheck, Calendar, CalendarClock, CalendarDays, ClipboardList,
  ClipboardCheck, Stethoscope, UserRound, Users, ChefHat, Grid2x2, Utensils, Receipt,
  FileText, Wrench, Truck, Hammer, HeartPulse, Scissors, Bed, Car, Camera, Music,
  Briefcase, Building2, Home, Key, Leaf, Pill, Plane, Ship, Ticket, Tag, Timer,
  Wallet, Warehouse, Zap, Star, Shield, Phone, MapPin, Book, Bookmark, Layers,
} from 'lucide-react';
import API_BASE_URL from '../config/api';

/* ── Icons ──────────────────────────────────────────────────────────────────
   A CURATED map, not `import * as Icons from 'lucide-react'`. A namespace
   import defeats tree-shaking and pulls the whole library into the bundle just
   to satisfy a name that arrives as a string. An unknown name falls back to
   `Box`, which is what the server already defaults `ObjectType.icon` to — so an
   object whose icon is not listed here looks deliberate, not broken. */
const ICONS = {
  Box, Boxes, Package, PackageCheck, Calendar, CalendarClock, CalendarDays, ClipboardList,
  ClipboardCheck, Stethoscope, UserRound, Users, ChefHat, Grid2x2, Utensils, Receipt,
  FileText, Wrench, Truck, Hammer, HeartPulse, Scissors, Bed, Car, Camera, Music,
  Briefcase, Building2, Home, Key, Leaf, Pill, Plane, Ship, Ticket, Tag, Timer,
  Wallet, Warehouse, Zap, Star, Shield, Phone, MapPin, Book, Bookmark, Layers,
};

/** The lucide component for an object's `icon` string. Never null. */
export const objectIconComponent = (name) => ICONS[name] || Box;

/** `<ObjectIcon name="ChefHat" size={20} />` — the icon, ready to render. */
export const ObjectIcon = ({ name, ...rest }) => {
  const Icon = objectIconComponent(name);
  return <Icon {...rest} />;
};

/* ── Status colours ─────────────────────────────────────────────────────────
   A status declares a `color` word; `atelier/StatusPill` speaks six tones.
   Anything unmapped becomes `gray` — a status with an odd colour still reads
   as a status rather than disappearing. */
const TONES = {
  green: 'green', emerald: 'green', teal: 'green',
  red: 'red', rose: 'red', pink: 'red',
  amber: 'amber', yellow: 'amber', orange: 'orange',
  blue: 'blue', sky: 'blue', indigo: 'blue', violet: 'blue', purple: 'blue',
  slate: 'gray', gray: 'gray', grey: 'gray', stone: 'gray', zinc: 'gray', neutral: 'gray',
};
export const statusTone = (color) => TONES[String(color || '').toLowerCase()] || 'gray';

/** The status descriptor for a key, out of a schema's `statuses` list. */
export const findStatus = (statuses, key) =>
  (statuses || []).find((s) => s && s.key === key) || null;

/* ── Emptiness ──────────────────────────────────────────────────────────────
   🔴 A MIRROR OF `object_fields.is_empty`, AND IT IS NOT FALSINESS.
   `0`, `0.00` and `false` are ANSWERS — a covers count of zero, a price of
   zero, a "no". This repo has shipped the falsy version of this test before
   (`_to_decimal(row.final_price) or …` rebuilt ₹0 exchanges at full value in
   GSTR-1) and there is a server-side test pinning the correct behaviour. The
   client must never be stricter than the server, so this is the only question
   the form's `required` check is allowed to ask. */
export const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return false;
  if (typeof value === 'number') return !Number.isFinite(value);
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/** Produced on read, refused on write — the form shows these, never edits them. */
export const COMPUTED_TYPES = ['formula', 'rollup'];
export const isComputed = (field) => COMPUTED_TYPES.includes(field && field.type);

/** The natural empty value for a field, by type. */
export const blankFor = (field) => {
  switch (field && field.type) {
    case 'multiselect':
    case 'tags':
    case 'images':
      return [];
    case 'toggle':
      return field.default === undefined ? false : Boolean(field.default);
    case 'relation':
    case 'file':
    case 'signature':
      return null;
    default:
      return !field || field.default === undefined || field.default === null ? '' : field.default;
  }
};

/** Fields a surface should render — `show_in` absent means "everywhere". */
export const fieldsFor = (schema, surface) =>
  (schema || []).filter((f) => {
    if (!f || !f.key) return false;
    const shown = f.show_in;
    if (!Array.isArray(shown) || shown.length === 0) return true;
    return shown.includes(surface);
  });

/** Every field keyed by its key — the lookup the list and detail cells use. */
export const fieldMap = (schema) => {
  const map = {};
  (schema || []).forEach((f) => { if (f && f.key) map[f.key] = f; });
  return map;
};

/* ── Server refusals ────────────────────────────────────────────────────────
   `object_views._errors` answers `{error, errors: [...]}` where every entry is
   a SENTENCE THAT STARTS WITH THE FIELD'S LABEL ("Number of guests is
   required.") — `object_fields` says so out loud, because the person reading it
   typed the label, not the key. So a message can be put back beside the field
   it came from by matching that prefix, which is the difference between an
   error next to the input and a toast that makes someone hunt.

   Longest label first: with fields "Name" and "Name of guest", a shortest-first
   match would hang "Name of guest is required." on "Name".

   The dict shape (`{errors: {key: message}}`) is accepted too, so this keeps
   working unchanged if the endpoint ever answers per key. */
export const splitErrors = (schema, payload) => {
  const byKey = {};
  const rest = [];
  if (!payload || typeof payload !== 'object') {
    return { byKey, rest: payload ? [String(payload)] : [] };
  }

  const raw = payload.errors;

  if (raw && !Array.isArray(raw) && typeof raw === 'object') {
    Object.entries(raw).forEach(([key, message]) => {
      const text = Array.isArray(message) ? message.join(' ') : String(message);
      if ((schema || []).some((f) => f && f.key === key)) byKey[key] = text;
      else rest.push(text);
    });
    return { byKey, rest };
  }

  const messages = Array.isArray(raw) && raw.length
    ? raw.map(String)
    : (payload.error ? [String(payload.error)] : []);

  const labelled = (schema || [])
    .filter((f) => f && f.key && f.label)
    .sort((a, b) => String(b.label).length - String(a.label).length);

  messages.forEach((message) => {
    const hit = labelled.find((f) => message.startsWith(String(f.label)));
    if (hit && !byKey[hit.key]) byKey[hit.key] = message;
    else rest.push(message);
  });

  return { byKey, rest };
};

/** One readable line out of any axios failure, for a banner. */
export const errorText = (err, fallback = 'Something went wrong.') => {
  const d = err && err.response ? err.response.data : null;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (d && d.error) return String(d.error);
  if (d && d.detail) return String(d.detail);
  if (d && Array.isArray(d.errors) && d.errors.length) return String(d.errors[0]);
  return (err && err.message) || fallback;
};

/** True when a failure is the feature being off (or the record being gone). */
export const isNotFound = (err) => err && err.response && err.response.status === 404;

/* ── The type list (module-scope cache) ─────────────────────────────────── */
let _types = null;        // null = never loaded; [] = loaded, and there are none
let _promise = null;
const _listeners = new Set();

export const objectTypes = () => _types || [];
export const objectTypesLoaded = () => _types !== null;

export const subscribeObjectTypes = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

/**
 * Fetch the type list once per page load.
 *
 * Resolves to `[]` on EVERY failure — the flag being off (404), a lapsed
 * session, an offline browser. The sidebar's only question is "are there any
 * custom objects", and the honest answer to all three is "none I can show".
 */
export const loadObjectTypes = () => {
  if (_types !== null) return Promise.resolve(_types);
  if (_promise) return _promise;
  _promise = axios
    .get(`${API_BASE_URL}/api/objects/`)
    .then((res) => (Array.isArray(res.data && res.data.objects) ? res.data.objects : []))
    .catch(() => [])
    .then((list) => {
      _types = list;
      _listeners.forEach((fn) => {
        try { fn(list); } catch { /* a bad listener must not break the nav */ }
      });
      return list;
    });
  return _promise;
};

/** Drop the cache so the next user does not inherit this one's objects. */
export const resetObjectTypes = () => { _types = null; _promise = null; };
